import { getAppEnv, isLocalEnvironment, normalizeErrorEvent, vars } from '@repo/config';
import * as Sentry from '@sentry/browser';
import { getEdgeTrace } from './edge-trace';

const SENTRY_ANON_ID_KEY = 'cm_sentry_anon';

let initialized = false;
let replayAttached = false;

function isCoarsePointer(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
}

function buildReplayIntegration(): ReturnType<typeof Sentry.replayIntegration> {
  // Unmasked replays - disclosed in privacy policy, terms, and cookie policy
  return Sentry.replayIntegration({
    maskAllText: false,
    blockAllMedia: false,
  });
}

// rrweb's always-on buffer recording (a MutationObserver firing on every DOM change)
// is a measurable mobile INP tax, so coarse-pointer devices start WITHOUT the replay
// integration and attach it on the first error: that first error carries no replay,
// but the session records from then on and any subsequent error uploads it.
function attachReplayOnFirstError(): void {
  if (replayAttached) {
    return;
  }
  replayAttached = true;
  Sentry.addIntegration(buildReplayIntegration());
}

export function initSentry(): void {
  if (initialized) {
    return;
  }
  initialized = true;

  if (isLocalEnvironment()) {
    return;
  }

  const coarsePointer = isCoarsePointer();
  replayAttached = !coarsePointer;

  Sentry.init({
    dsn: vars.sentry.webDsn,
    environment: getAppEnv(),
    // biome-ignore lint/style/noProcessEnv: Next inlines NEXT_PUBLIC_ build env into the client bundle
    release: process.env.NEXT_PUBLIC_SENTRY_RELEASE || undefined,
    enableLogs: true,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: coarsePointer ? 0 : 0.01,
    replaysOnErrorSampleRate: 1.0,
    integrations: coarsePointer ? [] : [buildReplayIntegration()],
    beforeSend,
  });

  void enrichScope();
}

function beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent | null {
  const message = event.exception?.values?.[0]?.value ?? '';
  // ResizeObserver loop notifications are benign browser noise, never actionable.
  if (message.includes('ResizeObserver loop')) {
    return null;
  }
  // zaloJSV2 is a global injected by the Zalo in-app browser, not our code.
  if (message.includes('zaloJSV2')) {
    return null;
  }
  // iOS in-app webviews inject sendDataToNative bridges that probe
  // window.webkit.messageHandlers; not our code, never actionable.
  if (message.includes('webkit.messageHandlers') || message.includes('sendDataToNative')) {
    return null;
  }
  attachReplayOnFirstError();
  return normalizeErrorEvent(event, hint);
}

interface ISentryUserParams {
  userId: string;
  email?: string;
  name?: string;
}

export function setSentryUser({ userId, email, name }: ISentryUserParams): void {
  Sentry.setUser({ id: userId, email, name });
}

export function setSentryAnonUser(): void {
  Sentry.setUser({ id: getAnonId() });
}

export function clearSentryUser(): void {
  Sentry.setUser(null);
}

export function enableDetectiveMode(): void {
  Sentry.getCurrentScope().setTag('detectiveMode', 'true');
}

export function setSentryLocale(locale: string): void {
  Sentry.getCurrentScope().setTag('locale', locale);
}

function getAnonId(): string {
  try {
    const existing = localStorage.getItem(SENTRY_ANON_ID_KEY);
    if (existing) {
      return existing;
    }
    const fresh = crypto.randomUUID();
    localStorage.setItem(SENTRY_ANON_ID_KEY, fresh);
    return fresh;
  } catch {
    return 'anonymous';
  }
}

async function enrichScope(): Promise<void> {
  try {
    const scope = Sentry.getCurrentScope();
    setDeviceTags(scope);
    const trace = await getEdgeTrace();
    if (trace.country) {
      scope.setTag('country', trace.country);
    }
    if (trace.colo) {
      scope.setTag('cf.colo', trace.colo);
    }
  } catch {
    // best-effort enrichment, never throw from instrumentation
  }
}

function setDeviceTags(scope: Sentry.Scope): void {
  const connection = (
    navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    }
  ).connection;
  if (connection?.effectiveType) {
    scope.setTag('net.effective_type', connection.effectiveType);
  }
  if (typeof connection?.saveData === 'boolean') {
    scope.setTag('net.save_data', connection.saveData);
  }
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  scope.setTag('display_mode', standalone ? 'standalone' : 'browser');
  scope.setTag('viewport', `${window.innerWidth}x${window.innerHeight}`);
  scope.setTag('dpr', window.devicePixelRatio);
  scope.setTag('reduced_motion', window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}
